// src/components/Profile.js
import React, { useContext, useEffect, useState } from 'react';
import { format } from 'date-fns';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import EditIcon from '@mui/icons-material/Edit';
import {
  Card,
  CardContent,
  Button,
  Stack,
  Box,
  Typography,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { ColorModeContext } from '../index';
import { request } from '../utils/request';

function Profile() {
  const { mode } = useContext(ColorModeContext);
  const textColor = mode === 'dark' ? '#ffffff' : '#333';
  const cardBg = mode === 'dark' ? '#2d3448' : '#fff';
  const navigate = useNavigate();

  const storedUser = localStorage.getItem('user');
  const user = storedUser ? JSON.parse(storedUser) : {};

  // Helpers
  const capitalize = (s = '') =>
    String(s)
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
      .trim();

  // Name formatting: "Amlan D."
  const rawName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim();
  let displayName = rawName || 'Unknown User';
  if (rawName) {
    const parts = rawName.trim().split(/\s+/);
    const f = parts[0] || '';
    const last = parts.slice(1).join(' ');
    if (last) {
      displayName = `${capitalize(f)} ${capitalize(last)[0] ? capitalize(last)[0] + '.' : ''}`;
    } else {
      displayName = capitalize(f);
    }
  }

  const role = user.role || '';
  const phone = user.mobile || user.phone || '—';
  const department = user.department || '—';
  const empId = user.emp_id || user.empId || user.empID || '—';
  const designation = user.designation || '—';

  // Avatar state
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const userId = user && (user._id || user.id);

    const fetchAvatar = async () => {
      if (!userId) return;
      setAvatarLoading(true);
      try {
        // Calls your endpoint: GET /api/v1/user/:id/avatar
        const data = await request('user', { method: 'get', params: `${userId}/avatar` });
        // expected shape: { url, expiresAt }
        if (mounted && data && data.url) {
          setAvatarUrl(data.url);
        }
      } catch (err) {
        // keep default letter avatar on error
        console.error('Failed to fetch avatar signed URL', err);
        setAvatarUrl(null);
      } finally {
        if (mounted) setAvatarLoading(false);
      }
    };

    fetchAvatar();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedUser]); // re-run when stored user changes

  // ---------------------------
  // New: fetch user's sessions and deviceIds
  // ---------------------------
  const [devices, setDevices] = useState([]); // [{ deviceId, lastSeen }]
  const [devicesLoading, setDevicesLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const userId = user && (user._id || user.id);
    if (!userId) {
      setDevices([]);
      return;
    }

    const fetchSessions = async () => {
      setDevicesLoading(true);
      try {
        // GET /api/v1/sessions?userId=<id>&limit=100
        const res = await request('sessions', { method: 'get', query: { userId, limit: 100 } });
        // res shape: { success: true, data: [...], meta: {...} }
        const sessions = (res && res.data) || [];
        // Build map of deviceId -> most recent loginTime
        const map = sessions.reduce((acc, s) => {
          const rawDevice = (s.deviceId || s.deviceid || (s.devicePosture && s.devicePosture.deviceId) || '').toString().trim();
          if (!rawDevice) return acc;
          const device = rawDevice;
          const loginTime = s.loginTime ? new Date(s.loginTime) : (s.createdAt ? new Date(s.createdAt) : null);
          const prev = acc[device];
          if (!prev || (loginTime && loginTime > prev.lastSeen)) {
            acc[device] = { deviceId: device, lastSeen: loginTime || new Date(0) };
          }
          return acc;
        }, {});
        const list = Object.values(map)
          .sort((a, b) => b.lastSeen - a.lastSeen) // newest first
          .map(it => ({ ...it, lastSeen: it.lastSeen instanceof Date ? it.lastSeen.toISOString() : it.lastSeen }));
        if (mounted) setDevices(list);
      } catch (err) {
        console.error('Failed to fetch sessions for devices:', err);
        if (mounted) setDevices([]);
      } finally {
        if (mounted) setDevicesLoading(false);
      }
    };

    fetchSessions();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedUser]);

  const detailIconMapping = {
    person: <PersonIcon sx={{ fontSize: 20, mr: 1, color: '#00bf63' }} />,
    badge: <BadgeIcon sx={{ fontSize: 20, mr: 1, color: '#00bf63' }} />,
  };

  // handler for edit profile navigation
  const goToEditProfile = () => {
    navigate('/user-management');
  };

  const ProfileSummary = ({ displayName, designation, email, phone }) => (
    <Box>
      <Card
        sx={{
          mb: 2,
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: cardBg,
        }}
      >
        <CardContent sx={{ textAlign: 'center', color: textColor, py: 2, position: 'relative' }}>
          {/* Avatar: image if available, otherwise first-letter circle */}
          <Box sx={{ mb: 1.5, position: 'relative', display: 'inline-block' }}>
            {avatarLoading ? (
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f0f0f0',
                }}
              >
                <CircularProgress size={28} />
              </Box>
            ) : avatarUrl ? (
              <Box
                component="img"
                src={avatarUrl}
                alt="profile"
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  mb: 0.5,
                  boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: '#00bf63',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '2rem',
                  mb: 0.5,
                  boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                  textTransform: 'uppercase',
                }}
              >
                {displayName ? displayName[0] : 'U'}
              </Box>
            )}

            {/* Edit icon overlay (small) */}
            <Tooltip title="Edit profile">
              <IconButton
                size="small"
                onClick={goToEditProfile}
                sx={{
                  position: 'absolute',
                  right: -6,
                  bottom: -6,
                  bgcolor: mode === 'dark' ? '#0b1220' : '#fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  '&:hover': { bgcolor: mode === 'dark' ? '#0b1220' : '#f4f6f8' },
                }}
                aria-label="edit profile"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography
            variant="h6"
            component="h2"
            sx={{ fontWeight: 700, color: textColor, mb: 0.5 }}
          >
            {displayName}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="body2" sx={{ color: textColor }}>
              {designation}
            </Typography>

            {/* subtle edit text button next to designation for discoverability */}
            <Button
              variant="outlined"
              size="small"
              onClick={goToEditProfile}
              sx={{
                textTransform: 'none',
                ml: 1,
                borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : undefined,
                color: mode === 'dark' ? '#fff' : undefined,
              }}
            >
              Edit Profile
            </Button>
          </Box>

          <Box
            sx={{
              borderTop: `1px solid ${mode === 'dark' ? '#444' : '#e0e0e0'}`,
              pt: 1.5,
            }}
          >
            <Stack spacing={1} alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ fontSize: 18, mr: 1, color: textColor }} />
                <Typography variant="body2" sx={{ color: textColor }}>
                  {user.email || '—'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon sx={{ fontSize: 18, mr: 1, color: textColor }} />
                <Typography variant="body2" sx={{ color: textColor }}>
                  {phone}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* New: Devices Card */}
      <Card
        sx={{
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: cardBg,
          mb: 2
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, color: textColor }}>
            Login Devices
          </Typography>

          {devicesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          ) : devices.length === 0 ? (
            <Typography variant="body2" sx={{ color: textColor }}>
              No devices found for this user.
            </Typography>
          ) : (
            <List dense>
              {devices.map((d, idx) => (
                <React.Fragment key={d.deviceId + '_' + idx}>
                  <ListItem disableGutters>
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 700, color: textColor }}>{d.deviceId}</Typography>}
                      secondary={
                        <Typography variant="caption" sx={{ color: textColor }}>
                          Last seen: {d.lastSeen ? format(new Date(d.lastSeen), 'PP p') : '—'}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {idx < devices.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Card
        sx={{
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: cardBg,
        }}
      >
        {/* Placeholder for additional quick-actions or other content */}
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={1}>
            {/* Removed the separate "Edit Profile" button as requested.
                Edit functionality is now available directly in the profile card (avatar overlay & small button). */}
            {/* <Button
              variant="outlined"
              component={Link}
              to="/user-management"
              sx={{ textTransform: 'none' }}
            >
              Edit Profile
            </Button> */}
            {/* <Button
              variant="text"
              component={Link}
              to="/profile/change-password"
              startIcon={<LockIcon />}
              sx={{ textTransform: 'none' }}
            >
              Change Password
            </Button> */}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );

  // Updated DetailCard: label (bold) and value on same line: "Label: Value"
  const DetailCard = ({ title, icon, items }) => (
    <Card
      sx={{
        boxShadow: 3,
        borderRadius: 2,
        mb: 2,
        backgroundColor: cardBg,
      }}
    >
      <CardContent sx={{ color: textColor, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          {detailIconMapping[icon]}
          <Typography variant="h6" sx={{ color: textColor }}>
            {title}
          </Typography>
        </Box>

        <Grid container spacing={1}>
          {items.map((item, idx) => (
            <Grid item xs={12} sm={6} key={idx}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: textColor, fontWeight: 700, mr: 0.5 }}
                >
                  {item.label}:
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', color: textColor }}>
                  {item.value || '—'}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        backgroundColor: mode === 'dark' ? '#1e2538' : '#f9fafb',
        minHeight: '100vh',
        py: 3,
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          px: 2,
          color: textColor,
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <ProfileSummary
              displayName={displayName}
              designation={designation}
              email={user.email}
              phone={phone}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <DetailCard
              title="Personal Details"
              icon="person"
              items={[
                { label: 'Name', value: displayName },
                { label: 'Email Address', value: user.email || '—' },
                { label: 'Phone Number', value: phone || '—' },
                { label: 'Department', value: department || '—' },
                { label: 'Employee ID', value: empId || '—' },
                { label: 'Designation', value: designation || '—' },
              ]}
            />
            <DetailCard
              title="Account Details"
              icon="badge"
              items={[
                { label: 'Role', value: role || '—' },
                { label: 'Username', value: user.username || user.name || '—' },
              ]}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default Profile;
